import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import i18n from 'i18n-js';
import clsx from 'clsx';
import { Visibility } from '@material-ui/icons';
import {
    Checkbox,
    Chip,
    FormControlLabel,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from '@material-ui/core';

import { BasicThreeDotsMenu, NothingFoundMessage, PopupConfirmation, SearchInput } from 'components/shared';
import agent from 'api/agent';
import { useStyles } from '../styles';
import { PagedList, RestaurantsGroupModel } from 'api/models';
import globalStore from 'store/globalStore';
import { RestaurantsTableProps } from './RestaurantsTable';
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import DeleteIcon from '@material-ui/icons/Delete';

interface ColumnFilters {
    name?: string;
    address?: string;
    zip?: string;
    city?: string;
}

export default function RestaurantsGroupsTable(props: RestaurantsTableProps) {
    const classes = useStyles();
    const history = useHistory();
    const page = useRef(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [showDisableConfirmation, setShowDisableConfirmation] = useState<RestaurantsGroupModel | null>(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<RestaurantsGroupModel | null>(null);
    const loaderRef = useRef<HTMLTableRowElement | null>(null);
    const [pagination, setPagination] = useState<PagedList<RestaurantsGroupModel>>({
        count: 0,
        list: [],
    });
    const [filters, setFilters] = useState<ColumnFilters>({
        name: '',
        address: '',
        zip: '',
        city: '',
    });
    const columns = [
        { id: 'name', label: 'Restaurant Name', minWidth: 250 },
        { id: 'address', label: 'Address', minWidth: 50 },
        { id: 'zip', label: 'Zip', minWidth: 50 },
        { id: 'city', label: 'City', minWidth: 50 },
        { id: 'actions', label: '', minWidth: 0 },
    ];

    const handleFilterChange = (field: keyof ColumnFilters, value: string) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    async function getRestaurantsGroups(filters: ColumnFilters, page = 0) {
        return await agent.Restaurants.GetRestaurantsGroups({
            ...filters,
            showCoolBoxes: props.isCoolBoxChecked,
            page,
        });
    }

    function handleSearch(filters: ColumnFilters) {
        page.current = 0;
        getRestaurantsGroups(filters).then((response) => {
            const total = response.count ?? response.list.length;
            setPagination(response);
            setHasMore(response.list.length < total);
        });
    }

    function handleClearFilter(field: keyof ColumnFilters) {
        page.current = 0;
        handleFilterChange(field, '');
        handleSearch({ ...filters, [field]: '' });
    }

    function prepareContextMenuItems(group: RestaurantsGroupModel) {
        const items: JSX.Element[] = [
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => handleEditGroup(group)}
            >
                <Visibility className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('button.viewAndEdit')} />
            </div>,
            group.isDisabled ? (
                <div className={classes.threeDotsMenuItemContainer} onClick={() => setShowDisableConfirmation(group)}>
                    <SettingsBackupRestoreIcon className={classes.threeDotsItemIcon} />
                    <ListItemText primary={i18n.t('button.restore')} />
                </div>
            ) : (
                <div className={classes.threeDotsMenuItemContainer} onClick={() => setShowDisableConfirmation(group)}>
                    <RemoveCircleOutlineIcon className={classes.threeDotsItemIcon} />
                    <ListItemText primary={i18n.t('button.disable')} />
                </div>
            ),
            <div className={classes.threeDotsMenuItemContainer} onClick={() => setShowDeleteConfirmation(group)}>
                <DeleteIcon className={classes.threeDotsItemIcon} />
                <ListItemText
                    primary={i18n.t('button.delete')}
                    className={classes.threeDotsItemIcon}
                />
            </div>,
        ];

        return items;
    }

    function handleEditGroup(group: RestaurantsGroupModel) {
        history.push(`/restaurants-group/${group.id}`);
    }

    function handleDisableGroup(group: RestaurantsGroupModel) {
        agent.Restaurants.DisableRestaurantGroup(group.id).then((res) => {
            if (group) {
                group.isDisabled = res.isDisabled;
                setPagination((prev) => ({
                    ...prev,
                    list: prev.list.map((g) => (g.id === group.id ? group : g)),
                }));
            }
        });
    }

    function handleDeleteGroup(group: RestaurantsGroupModel) {
        agent.Restaurants.DeleteRestaurantGroup(group.id)
            .then((id) => {
                setPagination((prev) => ({
                    count: prev.count - 1,
                    list: prev.list.filter((g) => g.id !== id),
                }));
            })
            .catch((error) => {
                globalStore.actionCreators.showToaster('error', error?.toString());
            });
    }

    const loadMore = useCallback(async () => {
        if (isLoading || !hasMore) return;

        setIsLoading(true);
        const nextPage = page.current + 1;

        try {
            const response = await getRestaurantsGroups(filters, nextPage);

            setPagination((prev) => {
                const newList = [...prev.list, ...response.list];
                const total = response.count ?? newList.length;
                const reachedEnd = newList.length >= total || response.list.length === 0;
                setHasMore(!reachedEnd);

                return {
                    ...prev,
                    list: newList,
                    count: total,
                };
            });

            if (response.list?.length) {
                page.current = nextPage;
            }
        } catch (e) {
            globalStore.actionCreators.showToaster('error', e?.toString());
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, hasMore, getRestaurantsGroups, setPagination]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    loadMore();
                }
            },
            { rootMargin: '200px' }
        );

        const el = loaderRef.current;
        if (el) observer.observe(el);

        return () => {
            if (el) observer.unobserve(el);
            observer.disconnect();
        };
    }, [loadMore]);

    useEffect(() => {
        getRestaurantsGroups(filters).then((response) => {
            setPagination(response);
        });
    }, [props.isCoolBoxChecked]);

    return (
        <>
            <FormControlLabel
                className={clsx(classes.tableFilers)}
                control={
                    <Checkbox
                        onChange={props.handleCoolboxCheckbox}
                        checked={props.isCoolBoxChecked}
                    />
                }
                label={i18n.t('restaurant.jamesCoolBox')}
            />
            <TableContainer style={{ flex: 1 }}>
                <Table
                    stickyHeader
                    className={classes.table}
                    aria-label="simple table"
                    style={{ height: pagination.count > 0 ? 'auto' : '100%' }}
                >
                    <TableHead className={classes.tableHead}>
                        {/* Header Row */}
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell sortDirection="asc" key={column.id}>
                                    {column.id !== 'actions' ? (
                                        <TableSortLabel hideSortIcon={true}>
                                            <SearchInput
                                                value={
                                                    filters[column.id as keyof ColumnFilters] || ''
                                                }
                                                name={column.label}
                                                onChange={(value: string) =>
                                                    handleFilterChange(
                                                        column.id as keyof ColumnFilters,
                                                        value
                                                    )
                                                }
                                                onEnter={() => handleSearch(filters)}
                                                onReset={() =>
                                                    handleClearFilter(
                                                        column.id as keyof ColumnFilters
                                                    )
                                                }
                                            />
                                        </TableSortLabel>
                                    ) : null}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pagination.count === 0 && (
                            <TableRow key={'no-data'}>
                                <TableCell colSpan={columns.length} style={{ height: '100%' }}>
                                    <NothingFoundMessage />
                                </TableCell>
                            </TableRow>
                        )}
                        {pagination.count > 0 &&
                            pagination.list.map((group) => (
                                <React.Fragment key={group.id}>
                                    {/* Group Separator Row */}
                                    <TableRow
                                        className={clsx(classes.tableGroupSeparator, {
                                            [classes.disabledRow]: group.isDisabled,
                                        })}
                                        hover={false}
                                        key={group.id}
                                        ref={loaderRef}
                                    >
                                        <TableCell colSpan={columns.length - 1} align="center">
                                            <Typography variant="body1">{group.name}</Typography>
                                        </TableCell>
                                        <TableCell
                                            padding="none"
                                            align="right"
                                            width={48}
                                            style={{ paddingRight: 8 }}
                                        >
                                            <BasicThreeDotsMenu
                                                items={prepareContextMenuItems(group)}
                                            />
                                        </TableCell>
                                    </TableRow>

                                    {/* Restaurants in Group */}
                                    {group.restaurants.map((restaurant) => (
                                        <TableRow
                                            key={restaurant.id}
                                            className={clsx({
                                                [classes.disabledRow]: group.isDisabled,
                                            })}
                                        >
                                            <TableCell style={{ minWidth: columns[0].minWidth }}>
                                                {restaurant.name}{' '}
                                                {restaurant.isCoolBox && (
                                                    <Chip
                                                        className={classes.smartFridgeLabel}
                                                        label={i18n.t('restaurant.jamesCoolBox')}
                                                        color="primary"
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell style={{ minWidth: columns[1].minWidth }}>
                                                {restaurant.address}
                                            </TableCell>
                                            <TableCell style={{ minWidth: columns[2].minWidth }}>
                                                {restaurant.zip}
                                            </TableCell>
                                            <TableCell style={{ minWidth: columns[3].minWidth }}>
                                                {restaurant.city}
                                            </TableCell>
                                            <TableCell style={{ width: columns[4].minWidth }} />
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <PopupConfirmation
                open={showDisableConfirmation !== null}
                close={() => setShowDisableConfirmation(null)}
                title={
                    showDisableConfirmation?.isDisabled
                        ? i18n.t('restaurant.restoreRestaurantGroup')
                        : i18n.t('restaurant.disableRestaurantGroup')
                }
                description={
                    showDisableConfirmation?.isDisabled
                        ? i18n.t('confirmation.sureToRestoreGroup')
                        : i18n.t('confirmation.sureToDisableGroup')
                }
                activeBtn={
                    showDisableConfirmation?.isDisabled
                        ? i18n.t('button.restore')
                        : i18n.t('common.disable')
                }
                action={() => handleDisableGroup(showDisableConfirmation!)}
            />
            <PopupConfirmation
                open={showDeleteConfirmation !== null}
                close={() => setShowDeleteConfirmation(null)}
                title={i18n.t('restaurant.deleteRestaurantGroup')}
                description={i18n.t('confirmation.sureToDeleteGroup')}
                activeBtn={i18n.t('common.delete')}
                action={() => handleDeleteGroup(showDeleteConfirmation!)}
            />
        </>
    );
}
